import React, { useState, useEffect } from "react";
import { usuarios, categoriasFinanceiras, centrosCusto } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Plus, Users, Wallet, Building, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatDateTime } from "../../lib/utils";
import { Badge } from "../ui/badge";

export function ConfiguracoesPage() {
  const [usuariosList, setUsuariosList] = useState([]);
  const [categoriasList, setCategoriasList] = useState([]);
  const [centrosList, setCentrosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isCatDialogOpen, setIsCatDialogOpen] = useState(false);
  const [isCCDialogOpen, setIsCCDialogOpen] = useState(false);
  const { isAdmin, user } = useAuth();

  const [userFormData, setUserFormData] = useState({ nome: "", email: "", senha: "", perfil: "leitura" });
  const [editUserFormData, setEditUserFormData] = useState({ nome: "", email: "", perfil: "leitura" });
  const [catFormData, setCatFormData] = useState({ nome: "", tipo: "despesa", descricao: "" });
  const [ccFormData, setCCFormData] = useState({ nome: "", descricao: "" });

  const fetchData = async () => {
    try {
      const [usersRes, catsRes, ccsRes] = await Promise.all([
        usuarios.list(),
        categoriasFinanceiras.list(),
        centrosCusto.list(),
      ]);
      setUsuariosList(usersRes.data);
      setCategoriasList(catsRes.data);
      setCentrosList(ccsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateUser = async (userId, data) => {
    try {
      await usuarios.update(userId, data);
      toast.success("Usuário atualizado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await usuarios.create(userFormData);
      toast.success("Usuário criado!");
      setIsUserDialogOpen(false);
      setUserFormData({ nome: "", email: "", senha: "", perfil: "leitura" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Deseja realmente excluir este usuário?")) return;
    try {
      await usuarios.delete(userId);
      toast.success("Usuário excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserFormData({
      nome: userToEdit.nome || "",
      email: userToEdit.email || "",
      perfil: userToEdit.perfil || "leitura",
    });
    setIsEditUserDialogOpen(true);
  };

  const handleSaveEditUser = async (e) => {
    e.preventDefault();
    try {
      await usuarios.update(editingUser.id, editUserFormData);
      toast.success("Usuário atualizado!");
      setIsEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar usuário");
    }
  };

  const handleCreateCategoria = async (e) => {
    e.preventDefault();
    try {
      await categoriasFinanceiras.create(catFormData);
      toast.success("Categoria criada!");
      setIsCatDialogOpen(false);
      setCatFormData({ nome: "", tipo: "despesa", descricao: "" });
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar");
    }
  };

  const handleCreateCentro = async (e) => {
    e.preventDefault();
    try {
      await centrosCusto.create(ccFormData);
      toast.success("Centro de custo criado!");
      setIsCCDialogOpen(false);
      setCCFormData({ nome: "", descricao: "" });
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar");
    }
  };

  const userColumns = [
    { key: "nome", header: "Nome" },
    { key: "email", header: "Email" },
    {
      key: "perfil",
      header: "Perfil",
      render: (v) => (
        <Badge variant="secondary" className={v === "administrador" ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-700"}>
          {v === "administrador" ? "Administrador" : "Leitura"}
        </Badge>
      ),
    },
    { key: "ultimo_acesso", header: "Último Acesso", render: (v) => formatDateTime(v) },
    { key: "status", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "220px",
      render: (_, row) =>
        isAdmin() && row.id !== user?.id && (
          <div className="flex items-center gap-2">
            <Select
              value={row.status}
              onValueChange={(v) => handleUpdateUser(row.id, { status: v })}
            >
              <SelectTrigger className="w-24 h-8" data-testid={`user-status-${row.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ativo">Ativo</SelectItem>
                <SelectItem value="inativo">Inativo</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEditUser(row)}
              data-testid={`edit-user-${row.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteUser(row.id)}
              data-testid={`delete-user-${row.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  const catColumns = [
    { key: "nome", header: "Nome" },
    {
      key: "tipo",
      header: "Tipo",
      render: (v) => (
        <Badge variant="secondary" className={v === "receita" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
          {v === "receita" ? "Receita" : "Despesa"}
        </Badge>
      ),
    },
    { key: "descricao", header: "Descrição" },
  ];

  const ccColumns = [
    { key: "nome", header: "Nome" },
    { key: "descricao", header: "Descrição" },
    { key: "status", header: "Status", render: (v) => <StatusBadge status={v} /> },
  ];

  return (
    <div className="space-y-6" data-testid="configuracoes-page">
      <div>
        <h1 className="text-3xl font-heading font-bold">Configurações</h1>
        <p className="text-muted-foreground">Gerencie usuários e parâmetros do sistema</p>
      </div>

      <Tabs defaultValue="usuarios" className="space-y-4">
        <TabsList>
          <TabsTrigger value="usuarios" data-testid="tab-usuarios">
            <Users className="h-4 w-4 mr-2" />
            Usuários
          </TabsTrigger>
          <TabsTrigger value="categorias" data-testid="tab-categorias">
            <Wallet className="h-4 w-4 mr-2" />
            Categorias Financeiras
          </TabsTrigger>
          <TabsTrigger value="centros" data-testid="tab-centros">
            <Building className="h-4 w-4 mr-2" />
            Centros de Custo
          </TabsTrigger>
        </TabsList>

        {/* Usuários Tab */}
        <TabsContent value="usuarios" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Usuários do Sistema</CardTitle>
              {isAdmin() && (
                <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-usuario-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Novo Usuário
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Novo Usuário</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nome *</Label>
                        <Input
                          value={userFormData.nome}
                          onChange={(e) => setUserFormData({ ...userFormData, nome: e.target.value })}
                          required
                          data-testid="usuario-nome"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={userFormData.email}
                          onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                          required
                          data-testid="usuario-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Senha *</Label>
                        <Input
                          type="password"
                          value={userFormData.senha}
                          onChange={(e) => setUserFormData({ ...userFormData, senha: e.target.value })}
                          required
                          data-testid="usuario-senha"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Perfil *</Label>
                        <Select value={userFormData.perfil} onValueChange={(v) => setUserFormData({ ...userFormData, perfil: v })}>
                          <SelectTrigger data-testid="usuario-perfil">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="administrador">Administrador</SelectItem>
                            <SelectItem value="leitura">Leitura</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(false)}>Cancelar</Button>
                        <Button type="submit" data-testid="save-usuario-btn">Criar</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              <DataTable
                columns={userColumns}
                data={usuariosList}
                loading={loading}
                emptyMessage="Nenhum usuário encontrado"
              />
            </CardContent>
          </Card>

          {/* Edit User Dialog */}
          <Dialog open={isEditUserDialogOpen} onOpenChange={(open) => {
            setIsEditUserDialogOpen(open);
            if (!open) setEditingUser(null);
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Usuário</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveEditUser} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={editUserFormData.nome}
                    onChange={(e) => setEditUserFormData({ ...editUserFormData, nome: e.target.value })}
                    required
                    data-testid="edit-usuario-nome"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editUserFormData.email}
                    onChange={(e) => setEditUserFormData({ ...editUserFormData, email: e.target.value })}
                    required
                    data-testid="edit-usuario-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Perfil *</Label>
                  <Select value={editUserFormData.perfil} onValueChange={(v) => setEditUserFormData({ ...editUserFormData, perfil: v })}>
                    <SelectTrigger data-testid="edit-usuario-perfil">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="administrador">Administrador</SelectItem>
                      <SelectItem value="leitura">Leitura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsEditUserDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-edit-usuario-btn">Salvar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Categorias Tab */}
        <TabsContent value="categorias" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Categorias Financeiras</CardTitle>
              {isAdmin() && (
                <Dialog open={isCatDialogOpen} onOpenChange={setIsCatDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-categoria-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Nova Categoria
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nova Categoria Financeira</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCategoria} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nome *</Label>
                        <Input
                          value={catFormData.nome}
                          onChange={(e) => setCatFormData({ ...catFormData, nome: e.target.value })}
                          required
                          data-testid="categoria-nome"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo *</Label>
                        <Select value={catFormData.tipo} onValueChange={(v) => setCatFormData({ ...catFormData, tipo: v })}>
                          <SelectTrigger data-testid="categoria-tipo">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="receita">Receita</SelectItem>
                            <SelectItem value="despesa">Despesa</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Descrição</Label>
                        <Input
                          value={catFormData.descricao}
                          onChange={(e) => setCatFormData({ ...catFormData, descricao: e.target.value })}
                          data-testid="categoria-descricao"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsCatDialogOpen(false)}>Cancelar</Button>
                        <Button type="submit" data-testid="save-categoria-btn">Criar</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              <DataTable
                columns={catColumns}
                data={categoriasList}
                loading={loading}
                emptyMessage="Nenhuma categoria encontrada"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Centros de Custo Tab */}
        <TabsContent value="centros" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Centros de Custo</CardTitle>
              {isAdmin() && (
                <Dialog open={isCCDialogOpen} onOpenChange={setIsCCDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-centro-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Novo Centro
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Novo Centro de Custo</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCentro} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nome *</Label>
                        <Input
                          value={ccFormData.nome}
                          onChange={(e) => setCCFormData({ ...ccFormData, nome: e.target.value })}
                          required
                          data-testid="centro-nome"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Descrição</Label>
                        <Input
                          value={ccFormData.descricao}
                          onChange={(e) => setCCFormData({ ...ccFormData, descricao: e.target.value })}
                          data-testid="centro-descricao"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsCCDialogOpen(false)}>Cancelar</Button>
                        <Button type="submit" data-testid="save-centro-btn">Criar</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              <DataTable
                columns={ccColumns}
                data={centrosList}
                loading={loading}
                emptyMessage="Nenhum centro de custo encontrado"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
